import { useState, useMemo, useEffect, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Users, Search, Download, RefreshCw, Loader2, Star, PhoneCall, Repeat, AlertTriangle,
  ArrowUpDown, ChevronLeft, ChevronRight, Home, Globe, Mail, Phone, Calendar,
  TrendingUp, Layers, MessageSquare, X, ChevronRight as Flecha, ArrowLeft, ExternalLink,
  EyeOff, Eye, Undo2,
} from 'lucide-react';
import { useApi } from '@/hooks/useApi';
import { api, type HuespedFila, type HuespedDetalle, type LlamadaDetalle } from '@/lib/api';
import { ContenidoLlamada } from './LlamadasListaPage';
import { classNames, formatDate, formatDateTime, formatCurrency, toCsv } from '@/lib/utils';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import Modal from '@/components/ui/Modal';
import BotonLlamar from '@/components/centralita/BotonLlamar';

/**
 * Los huéspedes, vistos como clientes.
 *
 * La diferencia con mirar `guests` a secas es toda la pantalla: Hostify crea
 * una ficha por reserva de canal —Booking se inventa un email nuevo cada vez—
 * así que quien vuelve cinco agostos seguidos son cinco huéspedes distintos
 * para el PMS. `guest_customers` los junta por teléfono, y aquí se le cruzan
 * encima las reseñas (qué nos puntuó) y las llamadas (de qué se quejó).
 *
 * Por eso hay columnas que no existen en Hostify: `fichas` dice de cuántas
 * fichas se compuso el cliente, y `revisar` marca los casos en los que los
 * nombres juntados no se parecen —familias compartiendo teléfono—, que se
 * señalan pero no se deshacen.
 */

const PAGE_SIZE = 50;

/** La reserva en Hostify, para abrirla sin tener que buscarla por el código. */
const HOSTIFY_RESERVA = 'https://app.hostify.com/reservations/view';
/** La ficha del huésped en Hostify, para ir al PMS sin buscar por el id. */
const HOSTIFY_HUESPED = 'https://app.hostify.com/guests/view';

/**
 * El canal, como marca.
 *
 * Los iconos se sirven desde el propio CRM, no enlazados a las plataformas.
 * Hotlinkear sus favicons se probó y no funciona: Booking los bloquea, y los
 * otros dos se quedaban colgados sin llegar a cargar ni a fallar, dejando el
 * hueco vacío. Además, pedirlos fuera contaría a esos dominios quién está
 * mirando qué ficha.
 *
 * El de Booking es una marca dibujada aquí porque es el único que no se pudo
 * descargar; los de Airbnb y Vrbo son sus favicons reales. Se conserva de todos
 * modos el respaldo de color y letra por si algún día falta el fichero.
 *
 * HomeAway y Abritel son marcas de Vrbo, y «Hostify internal channel account»
 * es lo que pone el PMS en las reservas que entran por nuestra web: los tres se
 * doblan sobre el icono que les corresponde en vez de dejarlos sin identificar.
 */
const MARCAS = [
  { id: 'airbnb',  patron: /airbnb/i,                                              nombre: 'Airbnb',      icono: '/canales/airbnb.ico'      ,  color: '#FF5A5F', letra: 'A' },
  { id: 'booking', patron: /booking/i,                                             nombre: 'Booking.com', icono: '/canales/booking.svg'    , color: '#003580', letra: 'B' },
  { id: 'vrbo',    patron: /vrbo|homeaway|abritel/i,                               nombre: 'Vrbo',        icono: '/canales/vrbo.ico'       ,    color: '#1F5DC8', letra: 'V' },
  { id: 'sereno',  patron: /direct|website|^web$|tu ?sereno|hostify internal/i,    nombre: 'tusereno.es', icono: '/favicon.svg',                        color: '#0B1D2E', letra: 'S' },
] as const;

/** Marcas presentes, sin repetir, a partir de los canales y los orígenes. */
function marcasDe(valores: string[]) {
  const vistas = new Set<string>();
  const salida: (typeof MARCAS)[number][] = [];
  for (const v of valores) {
    const m = MARCAS.find((x) => x.patron.test(v));
    if (m && !vistas.has(m.id)) {
      vistas.add(m.id);
      salida.push(m);
    }
  }
  return salida;
}

function IconoCanal({ marca }: { marca: (typeof MARCAS)[number] }) {
  const [falla, setFalla] = useState(false);
  if (falla) {
    return (
      <span
        title={marca.nombre}
        className="grid place-items-center h-5 w-5 rounded text-[10px] font-bold text-white"
        style={{ backgroundColor: marca.color }}
      >
        {marca.letra}
      </span>
    );
  }
  return (
    <img
      src={marca.icono}
      alt={marca.nombre}
      title={marca.nombre}
      onError={() => setFalla(true)}
      className="h-5 w-5 rounded object-contain"
    />
  );
}

const ORDENES: { valor: string; etiqueta: string }[] = [
  { valor: 'revenue', etiqueta: 'Ingresos' },
  { valor: 'stays', etiqueta: 'Estancias' },
  { valor: 'nights', etiqueta: 'Noches' },
  { valor: 'rating', etiqueta: 'Nota' },
  { valor: 'llamadas', etiqueta: 'Llamadas' },
  { valor: 'lastCheckOut', etiqueta: 'Última visita' },
  { valor: 'firstCheckIn', etiqueta: 'Cliente desde' },
  { valor: 'name', etiqueta: 'Nombre' },
];

/** Coma decimal y sin separador de miles: así el Excel español lo suma. */
function decimalEspanol(n: number | null | undefined): string {
  return n === null || n === undefined ? '' : n.toFixed(2).replace('.', ',');
}

/** Las notas ya vienen normalizadas a base 5 desde el servidor. */
function Estrellas({ nota }: { nota: number | null }) {
  if (nota === null) return <span className="text-sereno-dark/25">—</span>;
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <Star size={13} className={nota >= 4 ? 'text-amber-500 fill-amber-500' : 'text-amber-500'} />
      <span className="tabular-nums font-medium">{nota.toFixed(2)}</span>
    </span>
  );
}

function Tile({ icon, label, value, hint, tone }: {
  icon: ReactNode; label: string; value: ReactNode; hint?: string;
  tone?: 'default' | 'good' | 'warn';
}) {
  return (
    <div className="card p-3">
      <div className="flex items-center gap-1.5 text-xs font-medium text-sereno-dark/50">
        {icon}{label}
      </div>
      <div className={classNames(
        'text-xl font-bold mt-1 tabular-nums',
        tone === 'good' && 'text-emerald-600',
        tone === 'warn' && 'text-amber-600',
      )}>
        {value}
      </div>
      {hint && <div className="text-[11px] text-sereno-dark/40 mt-0.5 leading-snug">{hint}</div>}
    </div>
  );
}

function Etiqueta({ children, tone = 'neutral' }: { children: ReactNode; tone?: 'neutral' | 'good' | 'warn' | 'bad' }) {
  return (
    <span className={classNames(
      'inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium whitespace-nowrap',
      tone === 'neutral' && 'bg-sereno-blue/40 text-sereno-dark/70',
      tone === 'good' && 'bg-emerald-50 text-emerald-700 border border-emerald-200',
      tone === 'warn' && 'bg-amber-50 text-amber-700 border border-amber-200',
      tone === 'bad' && 'bg-rose-50 text-rose-700 border border-rose-200',
    )}>
      {children}
    </span>
  );
}

/**
 * Un huésped en móvil.
 *
 * La tabla del listado mide 1.132 px y en un teléfono cabe en 261: dejarla con
 * desplazamiento lateral obliga a arrastrar para ver cada cifra. Aquí la misma
 * fila se apila, con el nombre y las etiquetas arriba y las cinco cifras que se
 * miran —ingresos, estancias, noches, nota y llamadas— en una rejilla debajo.
 */
function TarjetaHuesped({
  f,
  onAbrir,
  onExcluir,
}: {
  f: HuespedFila;
  onAbrir: () => void;
  onExcluir: () => void;
}) {
  return (
    <div
      onClick={onAbrir}
      className="border-b border-gray-50 last:border-0 px-3 py-3 active:bg-sereno-blue/10 cursor-pointer"
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-medium flex items-center gap-1.5 flex-wrap">
            {f.name || <span className="text-sereno-dark/30">Sin nombre</span>}
            {f.isRepeat && <Etiqueta tone="good"><Repeat size={10} />repite</Etiqueta>}
            {f.fichas > 1 && <Etiqueta><Layers size={10} />{f.fichas}</Etiqueta>}
            {f.revisar && <Etiqueta tone="warn"><AlertTriangle size={10} />revisar</Etiqueta>}
            {f.llamadaNegativa && <Etiqueta tone="bad"><MessageSquare size={10} />queja</Etiqueta>}
          </div>
          <div className="text-xs text-sereno-dark/45 mt-0.5 truncate">
            {[f.phone ? `+${f.phone}` : null, f.emails[0], f.country].filter(Boolean).join(' · ')}
          </div>
        </div>
        {/* En táctil no hay «pasar por encima», así que el botón está siempre. */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onExcluir(); }}
          title={f.excluido ? 'Volver a incluir' : 'Excluir de la vista y de las métricas'}
          className={classNames(
            'grid place-items-center h-8 w-8 rounded shrink-0',
            f.excluido ? 'text-emerald-700' : 'text-sereno-dark/30',
          )}
        >
          {f.excluido ? <Undo2 size={15} /> : <EyeOff size={15} />}
        </button>
      </div>

      {/* En una línea y no en rejilla: con cinco columnas en 375 px los
          ingresos salían cortados («4681…»), que es justo lo que se mira. */}
      <div className="flex items-baseline flex-wrap gap-x-2.5 gap-y-1 mt-2">
        <span className="text-sm font-semibold tabular-nums">{formatCurrency(f.revenue)}</span>
        <span className="text-xs text-sereno-dark/55 tabular-nums">
          {f.stays} {f.stays === 1 ? 'estancia' : 'estancias'}
          {f.cancelled > 0 && <span className="text-sereno-dark/35"> (+{f.cancelled} canc.)</span>}
          {' · '}{f.nights} noches
        </span>
        {f.rating !== null && <Estrellas nota={f.rating} />}
        {f.llamadas > 0 && (
          <span className="inline-flex items-center gap-1 text-xs text-sereno-dark/55">
            <PhoneCall size={11} />{f.llamadas}
          </span>
        )}
      </div>

      <div className="text-[11px] text-sereno-dark/40 mt-1.5 flex flex-wrap gap-x-3">
        {f.lastCheckOut && <span>Última visita {formatDate(f.lastCheckOut)}</span>}
        {f.firstCheckIn && <span>desde {f.firstCheckIn.slice(0, 4)}</span>}
        {f.listings[0] && <span className="truncate max-w-full">{f.listings[0]}</span>}
      </div>
    </div>
  );
}

// ── Ficha ───────────────────────────────────────────────────────────────────

function Dato({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="flex items-start gap-2.5 py-2 border-b border-gray-50 last:border-0">
      <span className="mt-0.5 text-sereno-dark/30 shrink-0">{icon}</span>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-sereno-dark/50">{label}</div>
        <div className="text-sm break-words">{value || <span className="text-sereno-dark/30">—</span>}</div>
      </div>
    </div>
  );
}

function Ficha({
  id,
  onAbrirLlamada,
  onExcluir,
  onNombre,
}: {
  id: string;
  onAbrirLlamada: (id: string) => void;
  onExcluir: (id: string, excluido: boolean) => Promise<void>;
  /** Sube el nombre al modal, que lo usa de título. */
  onNombre: (nombre: string) => void;
}) {
  const { data, loading, error, refresh } = useApi<HuespedDetalle>(() => api.getHuesped(id), [id]);
  const [cambiando, setCambiando] = useState(false);

  // El título del modal es el nombre del huésped. La fila ya lo trae, pero
  // entrando por /huespedes?id=... no hay fila de la que sacarlo, así que lo
  // manda la propia ficha en cuanto carga.
  useEffect(() => {
    if (data?.name) onNombre(data.name);
  }, [data?.name, onNombre]);

  if (loading) return <LoadingSpinner />;
  if (error || !data) return <div className="text-sm text-rose-600 p-4">{error ?? 'No encontrado'}</div>;

  // La reseña se pega a su estancia: así se ve qué opinó de CADA visita, que es
  // más útil que una media suelta cuando alguien ha venido cinco veces.
  const resenaPorReserva = new Map(data.reviews.map((r) => [String(r.reservationId), r]));

  // Se promedia solo sobre las que TIENEN nota. Hay reseñas que son únicamente
  // texto, sin puntuación; contarlas en el divisor bajaría la media, y contar
  // reseñas y notas por separado daría NaN si ninguna tuviera nota.
  const hoy = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Madrid' });
  // Por la ENTRADA, no por la salida: quien entró el 20 y sale el 27 no está
  // «por llegar», está dentro del apartamento. Son dos estados distintos y los
  // dos importan al mirar una ficha.
  const porLlegar = (s: { checkIn: string | null }) => String(s.checkIn ?? '') > hoy;
  const enCurso = (s: { checkIn: string | null; checkOut: string | null }) =>
    String(s.checkIn ?? '') <= hoy && String(s.checkOut ?? '') > hoy;

  const notas = data.reviews.map((r) => r.rating).filter((n): n is number => n !== null);
  const notaMedia = notas.length
    ? Math.round((notas.reduce((a, b) => a + b, 0) / notas.length) * 100) / 100
    : null;
  const confirmadas = data.stays.filter((s) => s.status === 'accepted');
  const otras = data.stays.filter((s) => s.status !== 'accepted');

  return (
    <div className="space-y-5">
      {data.excluido && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
          <EyeOff size={16} className="mt-0.5 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            Este huésped está <strong>excluido</strong>: no aparece en el listado ni cuenta en
            ninguna métrica. Sus datos siguen intactos.
          </p>
        </div>
      )}
      {data.revisar && (
        <div className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2.5">
          <AlertTriangle size={16} className="mt-0.5 text-amber-600 shrink-0" />
          <p className="text-xs text-amber-800 leading-relaxed">
            Este cliente junta fichas con nombres que no se parecen
            (<strong>{data.names.join('», «')}</strong>). Suele ser una pareja o una familia
            compartiendo teléfono. Las fichas originales siguen intactas en Hostify.
          </p>
        </div>
      )}

      {/* La acción va ARRIBA. Estuvo al final del modal y no había quien la
          encontrase: hay que pasar las estancias y las llamadas para llegar. */}
      <div className="flex justify-end -mb-2">
        <button
          type="button"
          disabled={cambiando}
          onClick={async () => {
            setCambiando(true);
            try {
              await onExcluir(data.id, !data.excluido);
              refresh();
            } finally {
              setCambiando(false);
            }
          }}
          className={classNames(
            'inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium',
            'transition-colors disabled:opacity-40 shrink-0',
            data.excluido
              ? 'border-emerald-300 text-emerald-700 hover:bg-emerald-50'
              : 'border-sereno-dark/15 text-sereno-dark/60 hover:border-amber-300 hover:text-amber-700',
          )}
        >
          {data.excluido ? <><Undo2 size={13} />Volver a incluir</> : <><EyeOff size={13} />Excluir de las métricas</>}
        </button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Tile icon={<Home size={13} />} label="Estancias" value={data.stats.confirmed}
          hint={data.stats.cancelled ? `${data.stats.cancelled} canceladas` : undefined} />
        <Tile icon={<Calendar size={13} />} label="Noches" value={data.stats.nights} />
        <Tile icon={<TrendingUp size={13} />} label="Ingresos"
          value={formatCurrency(data.stats.revenue)} hint="neto tras comisiones de canal" />
        <Tile icon={<Star size={13} />} label="Nota media"
          value={<Estrellas nota={notaMedia} />}
          hint={data.reviews.length ? `${data.reviews.length} reseña(s)` : 'sin reseñas'} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6">
        <Dato icon={<Phone size={14} />} label="Teléfono"
          value={data.phone ? (
            <span className="inline-flex items-center gap-1.5">
              +{data.phone}
              {/* Llamar desde aquí, sin copiar el número a la centralita. El
                  nombre y el alojamiento viajan con la llamada para que la barra
                  diga a quién se está llamando: Connect solo conoce el número. */}
              <BotonLlamar
                numero={data.phone}
                nombre={data.name}
                alojamiento={data.stats.listings?.[0] ?? null}
                compacto
              />
            </span>
          ) : ''} />
        <Dato icon={<Mail size={14} />} label="Email(s)" value={data.emails.join(', ')} />
        <Dato icon={<Globe size={14} />} label="País / ciudad"
          value={[data.country, data.city].filter(Boolean).join(' · ')} />
        <Dato icon={<Layers size={14} />} label="Fichas en Hostify"
          value={
            <span className="font-mono text-xs">
              {data.guestIds.map((gid, i) => (
                <span key={gid}>
                  {i > 0 && ', '}
                  <a
                    href={`${HOSTIFY_HUESPED}/${encodeURIComponent(gid)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    title="Abrir la ficha en Hostify"
                    className="hover:underline decoration-sereno-dark/30 underline-offset-2"
                  >
                    {gid}
                  </a>
                </span>
              ))}
            </span>
          } />
        <Dato icon={<Repeat size={14} />} label="Unificado por"
          value={data.matchedBy === 'telefono' ? 'Teléfono'
            : data.matchedBy === 'email' ? 'Email' : 'Sin clave (ficha única)'} />
        <Dato icon={<Globe size={14} />} label="Canales"
          value={(() => {
            const marcas = marcasDe([...data.channels, ...(data.stats.sources ?? [])]);
            return marcas.length ? (
              <span className="inline-flex items-center gap-1.5 mt-0.5">
                {marcas.map((m) => <IconoCanal key={m.id} marca={m} />)}
              </span>
            ) : null;
          })()} />
      </div>

      <div>
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
          <Home size={14} />Estancias ({confirmadas.length})
        </h3>
        {/* La tabla de estancias mide 638 px y en móvil cabe en 295: se apila. */}
        <div className="sm:hidden space-y-2">
          {confirmadas.map((s) => {
            const r = resenaPorReserva.get(String(s.reservationId));
            return (
              <div key={s.reservationId} className="rounded-lg border border-gray-100 p-2.5">
                <div className="flex items-start justify-between gap-2">
                  <a
                    href={`${HOSTIFY_RESERVA}/${encodeURIComponent(s.reservationId)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-xs font-medium inline-flex items-center gap-1"
                  >
                    {s.checkIn ? formatDate(s.checkIn) : '—'} → {s.checkOut ? formatDate(s.checkOut) : '—'}
                    <ExternalLink size={11} className="text-sereno-dark/30" />
                  </a>
                  <span className="text-xs tabular-nums shrink-0">
                    {s.payout !== null ? formatCurrency(s.payout) : '—'}
                  </span>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 mt-1">
                  {enCurso(s) && <Etiqueta tone="good">alojado ahora</Etiqueta>}
                  {porLlegar(s) && <Etiqueta tone="neutral">por llegar</Etiqueta>}
                  <span className="text-[11px] text-sereno-dark/50">
                    {[s.nights ? `${s.nights} noches` : null, s.listingNickname, s.source]
                      .filter(Boolean).join(' · ')}
                  </span>
                </div>
                {r && (
                  <div className="mt-1.5 pt-1.5 border-t border-gray-50">
                    <Estrellas nota={r.rating} />
                    {r.comments && (
                      <p className="text-[11px] text-sereno-dark/60 mt-0.5 leading-snug">{r.comments}</p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="hidden sm:block overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-sereno-dark/50 border-b border-gray-100">
                <th className="py-1.5 pr-3 font-medium">Entrada</th>
                <th className="py-1.5 pr-3 font-medium">Salida</th>
                <th className="py-1.5 pr-3 font-medium text-right">Noches</th>
                <th className="py-1.5 pr-3 font-medium">Alojamiento</th>
                <th className="py-1.5 pr-3 font-medium">Canal</th>
                <th className="py-1.5 pr-3 font-medium text-right">Neto</th>
                <th className="py-1.5 font-medium">Reseña</th>
              </tr>
            </thead>
            <tbody>
              {confirmadas.map((s) => {
                const r = resenaPorReserva.get(String(s.reservationId));
                return (
                  <tr key={s.reservationId} className="border-b border-gray-50 last:border-0 align-top">
                    <td className="py-1.5 pr-3 whitespace-nowrap">
                      {/* La fecha de entrada abre la reserva en Hostify: es la
                          columna que identifica la fila y evita añadir otra. */}
                      <a
                        href={`${HOSTIFY_RESERVA}/${encodeURIComponent(s.reservationId)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Abrir en Hostify${s.confirmationCode ? ` · ${s.confirmationCode}` : ''}`}
                        className="inline-flex items-center gap-1 text-sereno-dark hover:underline
                                   decoration-sereno-dark/30 underline-offset-2"
                      >
                        {s.checkIn ? formatDate(s.checkIn) : s.reservationId}
                        <ExternalLink size={11} className="text-sereno-dark/30 shrink-0" />
                      </a>
                      {enCurso(s) && <span className="ml-1.5"><Etiqueta tone="good">alojado ahora</Etiqueta></span>}
                      {porLlegar(s) && <span className="ml-1.5"><Etiqueta tone="neutral">por llegar</Etiqueta></span>}
                    </td>
                    <td className="py-1.5 pr-3 whitespace-nowrap">{s.checkOut ? formatDate(s.checkOut) : '—'}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{s.nights ?? '—'}</td>
                    <td className="py-1.5 pr-3">{s.listingNickname ?? <span className="text-sereno-dark/30">—</span>}</td>
                    <td className="py-1.5 pr-3">{s.source ?? '—'}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {s.payout !== null ? formatCurrency(s.payout) : '—'}
                    </td>
                    <td className="py-1.5 max-w-xs">
                      {r ? (
                        <div>
                          <Estrellas nota={r.rating} />
                          {r.comments && (
                            <p className="text-[11px] text-sereno-dark/60 mt-0.5 leading-snug">{r.comments}</p>
                          )}
                        </div>
                      ) : <span className="text-sereno-dark/25">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {otras.length > 0 && (
          <p className="text-[11px] text-sereno-dark/40 mt-2">
            Además {otras.length} reserva(s) que no llegaron a estancia (canceladas, caducadas o consultas).
          </p>
        )}
      </div>

      {data.calls.length > 0 && (
        <div>
          <h3 className="text-sm font-semibold mb-2 flex items-center gap-1.5">
            <PhoneCall size={14} />Llamadas ({data.calls.length})
          </h3>
          <div className="space-y-0.5">
            {data.calls.map((c) => (
              /* Cada llamada abre su detalle completo —transcripción, audio y
                 curva de sentimiento— reutilizando el mismo componente que la
                 sección Llamadas, en vez de mandar al usuario a buscarla allí. */
              <button
                key={c.id}
                type="button"
                onClick={() => onAbrirLlamada(c.id)}
                className="w-full flex items-center gap-2 flex-wrap text-xs border-b border-gray-50 py-2 last:border-0
                           text-left rounded hover:bg-sereno-blue/15 focus:outline-none focus:ring-1
                           focus:ring-sereno-dark/20 transition-colors px-1 -mx-1"
              >
                <span className="text-sereno-dark/50 whitespace-nowrap">{formatDateTime(c.startedAt)}</span>
                {c.categoria && <Etiqueta>{c.categoria.replace(/_/g, ' ')}</Etiqueta>}
                {c.sentimientoGlobal && (
                  <Etiqueta tone={c.sentimientoGlobal === 'NEGATIVE' ? 'bad'
                    : c.sentimientoGlobal === 'POSITIVE' ? 'good' : 'neutral'}>
                    {c.sentimientoGlobal.toLowerCase()}
                  </Etiqueta>
                )}
                {c.resuelto === false && <Etiqueta tone="warn">sin resolver</Etiqueta>}
                <span className="text-sereno-dark/40 ml-auto whitespace-nowrap">{c.direccion ?? ''}</span>
                <Flecha size={14} className="text-sereno-dark/25 shrink-0" />
              </button>
            ))}
          </div>
          <p className="text-[11px] text-sereno-dark/35 mt-1.5">
            Pulsa una llamada para ver su transcripción, la grabación y el análisis.
          </p>
        </div>
      )}

      <p className="text-[11px] text-sereno-dark/35">
        Ficha recalculada el {formatDateTime(data.updatedAt)}.
      </p>
    </div>
  );
}

// ── Página ──────────────────────────────────────────────────────────────────

export default function HuespedesPage() {
  const [q, setQ] = useState('');
  const [busqueda, setBusqueda] = useState('');
  const [page, setPage] = useState(1);
  const [orden, setOrden] = useState('revenue');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const [repetidores, setRepetidores] = useState(false);
  const [conLlamadas, setConLlamadas] = useState(false);
  const [revisar, setRevisar] = useState(false);
  const [verExcluidos, setVerExcluidos] = useState(false);
  const [canal, setCanal] = useState('');
  const [alojamiento, setAlojamiento] = useState('');
  const [abierto, setAbierto] = useState<string | null>(null);
  const [nombreAbierto, setNombreAbierto] = useState('');
  // `/huespedes?id=tel-600...` abre esa ficha directamente. Es a donde apunta la
  // agenda de la centralita, para poder saltar del contacto a su histórico.
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const id = params.get('id');
    if (id) {
      setAbierto(id);
      setNombreAbierto('');
      setParams({}, { replace: true });
    }
  }, [params, setParams]);
  // La llamada se enseña DENTRO del mismo modal, no en otro encima: apilar
  // diálogos complica el foco y el escape, y así no se pierde de vista de quién
  // es la ficha que se estaba mirando.
  const [llamada, setLlamada] = useState<LlamadaDetalle | null>(null);
  const [cargandoLlamada, setCargandoLlamada] = useState(false);
  const [refrescando, setRefrescando] = useState(false);
  const [errorRefresco, setErrorRefresco] = useState<string | null>(null);

  const resumen = useApi(() => api.getHuespedesResumen(), []);
  const listado = useApi(
    () => api.getHuespedes({
      q: busqueda, page, pageSize: PAGE_SIZE, orden, dir,
      repetidores, conLlamadas, revisar, canal, alojamiento, excluidos: verExcluidos,
    }),
    [busqueda, page, orden, dir, repetidores, conLlamadas, revisar, canal, alojamiento, verExcluidos],
  );

  const filtrosActivos = repetidores || conLlamadas || revisar || verExcluidos || Boolean(canal) || Boolean(alojamiento) || Boolean(busqueda);

  const buscar = (e: React.FormEvent) => {
    e.preventDefault();
    setBusqueda(q.trim());
    setPage(1);
  };

  const limpiar = () => {
    setQ(''); setBusqueda(''); setRepetidores(false); setConLlamadas(false);
    setRevisar(false); setVerExcluidos(false); setCanal(''); setAlojamiento(''); setPage(1);
  };

  const cambiarOrden = (valor: string) => {
    if (valor === orden) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setOrden(valor); setDir('desc'); }
    setPage(1);
  };

  /**
   * La primera llamada va con `refrescar=1` solo por su efecto en el servidor:
   * rehace la caché compartida, y los dos `refresh()` siguientes ya leen la
   * versión nueva. El error se captura aquí porque ocurre FUERA de `useApi`, y
   * sin capturarlo el botón se apagaba dejando en pantalla las cifras viejas
   * como si acabaran de actualizarse.
   */
  const refrescar = async () => {
    setRefrescando(true);
    setErrorRefresco(null);
    try {
      await api.getHuespedesResumen(true);
      resumen.refresh();
      listado.refresh();
    } catch (e) {
      setErrorRefresco(e instanceof Error ? e.message : 'No se pudo actualizar');
    } finally {
      setRefrescando(false);
    }
  };

  const abrirLlamada = async (id: string) => {
    setCargandoLlamada(true);
    try {
      setLlamada(await api.getLlamada(id));
    } catch {
      setLlamada(null);
    } finally {
      setCargandoLlamada(false);
    }
  };

  const cambiarExclusion = async (id: string, excluido: boolean) => {
    await api.excluirHuesped(id, excluido);
    // El resumen y el listado cambian los dos: un excluido sale de las cifras
    // de arriba además de desaparecer de la tabla.
    resumen.refresh();
    listado.refresh();
  };

  const exportar = () => {
    const items = listado.data?.items ?? [];
    const csv = toCsv([
      ['Cliente', 'Teléfono', 'Email', 'País', 'Fichas', 'Estancias', 'Noches',
       'Ingresos', 'Nota', 'Reseñas', 'Llamadas', 'Cliente desde', 'Última salida',
       'Canales', 'Alojamientos'],
      ...items.map((f) => [
        f.name, f.phone ? `+${f.phone}` : '', f.emails.join(' / '), f.country ?? '',
        f.fichas, f.stays, f.nights,
        // Con punto decimal el Excel español no los lee como número: «1234.56»
        // cae a texto y no suma, y «4.5» lo interpreta como la fecha 4 de mayo.
        // Mismo apaño que en ServiciosPage.
        decimalEspanol(f.revenue), decimalEspanol(f.rating),
        f.reviews, f.llamadas,
        f.firstCheckIn ?? '', f.lastCheckOut ?? '', f.sources.join(' / '), f.listings.join(' / '),
      ]),
    ]);
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8;' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `huespedes-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const r = resumen.data;
  const totalPaginas = Math.max(1, Math.ceil((listado.data?.total ?? 0) / PAGE_SIZE));

  /** La barra más larga es la mejor tasa de retorno, no un 100% teórico. */
  const tasaMax = useMemo(
    () => Math.max(1, ...(r?.cohortes ?? []).map((c) => c.pct)),
    [r],
  );

  return (
    <div className="p-4 sm:p-6 max-w-[1600px] mx-auto">
      {/* En móvil el título y los botones no caben en una línea: se apilan, y
          el subtítulo se guarda porque ocupa cuatro renglones de pantalla. */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 sm:mb-6 gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <Users size={24} className="text-sereno-dark shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold">Huéspedes</h1>
            <p className="hidden sm:block text-sm text-sereno-dark/40 mt-0.5">
              Personas, no reservas: las fichas que Hostify crea por canal, ya unificadas
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={refrescar} disabled={refrescando} className="btn-ghost flex items-center gap-2 text-sm">
            {refrescando ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            <span className="hidden sm:inline">Actualizar</span>
          </button>
          <button onClick={exportar} disabled={!listado.data?.items.length}
            className="btn-primary flex items-center gap-2 text-sm disabled:opacity-40">
            <Download size={16} />
            <span className="hidden sm:inline">Exportar página</span>
            <span className="sm:hidden">CSV</span>
          </button>
        </div>
      </div>

      {resumen.loading && !r && <LoadingSpinner />}
      {resumen.error && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 mb-4 text-sm text-rose-700">
          {resumen.error}
        </div>
      )}
      {errorRefresco && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 mb-4 text-sm text-rose-700">
          {errorRefresco} — <strong>las cifras siguen siendo las anteriores</strong>, no se han actualizado.
        </div>
      )}

      {r && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
            <Tile icon={<Users size={13} />} label="Clientes" value={r.conEstancia.toLocaleString('es-ES')}
              hint={`${r.clientes.toLocaleString('es-ES')} fichas unificadas · ${r.fusionados} de varias`} />
            <Tile icon={<Repeat size={13} />} label="Repetidores" value={r.repetidores} tone="good"
              hint={`${r.pctRepetidores}% de los clientes con estancia` +
                (r.repetidoresPorLlegar ? ` · ${r.repetidoresPorLlegar} aún no han vuelto, la 2ª está reservada` : '')} />
            <Tile icon={<TrendingUp size={13} />} label="Ingreso de repetidores"
              value={formatCurrency(r.ingresoRepetidores)} tone="good"
              hint={`${r.pctIngresoRepetidores}% del total`} />
            <Tile icon={<Star size={13} />} label="Nota media"
              value={<Estrellas nota={r.ratingMedio} />}
              hint={`${r.conResena.toLocaleString('es-ES')} clientes con reseña · base 5`} />
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Tile icon={<TrendingUp size={13} />} label="Valor medio por cliente"
              value={formatCurrency(r.valorMedio)}
              hint={r.ingresoPorLlegar
                ? `incluye ${formatCurrency(r.ingresoPorLlegar)} de ${r.estanciasPorLlegar} estancias aún sin empezar` +
                  (r.estanciasEnCurso ? ` · ${r.estanciasEnCurso} alojados ahora` : '')
                : undefined} />
            <Tile icon={<Repeat size={13} />} label="Valor medio de un repetidor"
              value={formatCurrency(r.valorMedioRepetidor)} tone="good"
              hint={r.valorMedio ? `×${(r.valorMedioRepetidor / r.valorMedio).toFixed(1)} frente al cliente medio` : undefined} />
            <Tile icon={<PhoneCall size={13} />} label="Han llamado" value={r.conLlamadas}
              hint="cruzado con la centralita por teléfono" />
            <Tile icon={<AlertTriangle size={13} />} label="A revisar" value={r.revisar}
              tone={r.revisar ? 'warn' : undefined}
              hint={`nombres dispares con el mismo teléfono` +
                (r.excluidos ? ` · ${r.excluidos} excluido(s) a mano, fuera de estas cifras` : '')} />
          </div>

          {r.cohortes.length > 1 && (
            <div className="card p-4 mb-4">
              <h2 className="text-sm font-semibold mb-1">¿Vuelven?</h2>
              <p className="text-xs text-sereno-dark/40 mb-3">
                De los clientes que vinieron por primera vez cada año, cuántos han vuelto alguna vez desde entonces.
                Los años recientes salen bajos por fuerza: aún no han tenido tiempo de volver.
              </p>
              <div className="space-y-1.5">
                {r.cohortes.map((c) => (
                  <div key={c.anyo} className="flex items-center gap-3 text-xs">
                    <span className="w-10 shrink-0 tabular-nums text-sereno-dark/60">{c.anyo}</span>
                    <div className="flex-1 h-4 bg-sereno-blue/20 rounded overflow-hidden">
                      {/* La barra es la TASA de retorno, no el tamaño de la
                          cohorte: con 40 clientes en 2019 frente a 1.171 en
                          2024, dibujar el tamaño dejaba los años antiguos como
                          un hilo invisible y la pregunta de la sección —si
                          vuelven— sin responder. El tamaño va al lado, en texto,
                          que es donde se juzga si la cifra es significativa. */}
                      <div className="h-full bg-emerald-500/70 rounded"
                        style={{ width: `${(c.pct / tasaMax) * 100}%` }} />
                    </div>
                    <span className="w-24 sm:w-36 shrink-0 text-right tabular-nums text-sereno-dark/60">
                      <strong className="text-emerald-600">{c.pct}%</strong>
                      <span className="text-sereno-dark/40"> · {c.repiten} de {c.clientes}</span>
                    </span>
                    {/* La nota de muestra corta se cae en móvil: el dato que
                        importa —cuántos de cuántos— ya está al lado. */}
                    {c.clientes < 100 && (
                      <span className="hidden sm:inline w-16 shrink-0 text-[10px] text-sereno-dark/30">muestra corta</span>
                    )}
                    {c.clientes >= 100 && <span className="hidden sm:inline w-16 shrink-0" />}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Filtros */}
      <div className="card p-3 mb-4 space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <form onSubmit={buscar} className="flex items-center gap-2 flex-1 w-full sm:w-auto sm:min-w-[240px]">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-sereno-dark/30" />
              <input value={q} onChange={(e) => setQ(e.target.value)}
                placeholder="Nombre, email o teléfono…"
                className="input pl-8 w-full text-sm" />
            </div>
            <button type="submit" className="btn-secondary text-sm">Buscar</button>
          </form>

          <select value={canal} onChange={(e) => { setCanal(e.target.value); setPage(1); }}
            className="input text-sm w-full sm:w-auto sm:max-w-[160px]">
            <option value="">Todos los canales</option>
            {(r?.canales ?? []).map((c) => (
              <option key={c.nombre} value={c.nombre}>{c.nombre} ({c.total})</option>
            ))}
          </select>

          <select value={alojamiento} onChange={(e) => { setAlojamiento(e.target.value); setPage(1); }}
            className="input text-sm w-full sm:w-auto sm:max-w-[200px]">
            <option value="">Todos los alojamientos</option>
            {(r?.alojamientos ?? []).map((a) => (
              <option key={a.nombre} value={a.nombre}>{a.nombre} ({a.total})</option>
            ))}
          </select>
        </div>

        {/* Sin tabla no hay cabeceras que pulsar para ordenar. */}
        <div className="lg:hidden flex items-center gap-2">
          <select
            value={orden}
            onChange={(e) => { setOrden(e.target.value); setPage(1); }}
            className="input text-sm flex-1"
            aria-label="Ordenar por"
          >
            {ORDENES.map((o) => <option key={o.valor} value={o.valor}>Ordenar por {o.etiqueta.toLowerCase()}</option>)}
          </select>
          <button
            type="button"
            onClick={() => { setDir((d) => (d === 'asc' ? 'desc' : 'asc')); setPage(1); }}
            title={dir === 'asc' ? 'De menor a mayor' : 'De mayor a menor'}
            className="btn-secondary text-sm px-2.5 shrink-0"
          >
            <ArrowUpDown size={15} />
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {([
            { on: repetidores, set: setRepetidores, label: 'Solo repetidores', icon: <Repeat size={13} /> },
            { on: conLlamadas, set: setConLlamadas, label: 'Han llamado', icon: <PhoneCall size={13} /> },
            { on: revisar, set: setRevisar, label: 'A revisar', icon: <AlertTriangle size={13} /> },
            { on: verExcluidos, set: setVerExcluidos, label: 'Excluidos', icon: <Eye size={13} /> },
          ] as const).map((f) => (
            <button key={f.label}
              onClick={() => { f.set(!f.on); setPage(1); }}
              className={classNames(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition',
                f.on ? 'bg-sereno-dark text-white border-sereno-dark'
                     : 'border-sereno-dark/15 text-sereno-dark/60 hover:border-sereno-dark/40',
              )}>
              {f.icon}{f.label}
            </button>
          ))}
          {filtrosActivos && (
            <button onClick={limpiar}
              className="inline-flex items-center gap-1 text-xs text-sereno-dark/50 hover:text-sereno-dark">
              <X size={13} />Limpiar
            </button>
          )}
          <span className="ml-auto text-xs text-sereno-dark/40">
            {listado.data ? `${listado.data.total.toLocaleString('es-ES')} resultado(s)` : ''}
          </span>
        </div>
      </div>

      {/* Listado */}
      {listado.loading && !listado.data && <LoadingSpinner />}
      {listado.error && (
        <div className="rounded-lg bg-rose-50 border border-rose-200 px-3 py-2.5 text-sm text-rose-700">
          {listado.error}
        </div>
      )}
      {listado.data && listado.data.items.length === 0 && (
        <EmptyState title="Ningún huésped" description="Prueba a quitar filtros o a buscar otra cosa." />
      )}

      {listado.error && listado.data && (
        <p className="text-xs text-amber-700 mb-2">
          La tabla de abajo es la última que se pudo cargar; no refleja el filtro ni la página actuales.
        </p>
      )}
      {listado.data && listado.data.items.length > 0 && (
        <div className={classNames('card overflow-hidden relative', listado.error && 'opacity-60')}>
          {/* Un refetch no vacía `data`, así que sin esto la tabla enseñaba las
              filas viejas mientras el pie ya decía la página nueva. */}
          {listado.loading && (
            <div className="absolute inset-0 z-10 bg-sereno-white/60 dark:bg-sereno-dark/60 flex items-start justify-center pt-8">
              <Loader2 size={22} className="animate-spin text-sereno-dark/50" />
            </div>
          )}
          {/* Tarjetas hasta 1024 px, tabla por encima. El corte no es `sm`
              sino `lg` porque la tabla tiene diez columnas y mide 1.132 px: en
              una tableta de 768 seguía arrastrando 494 px de lado. */}
          <div className="lg:hidden">
            {listado.data.items.map((f: HuespedFila) => (
              <TarjetaHuesped
                key={f.id}
                f={f}
                onAbrir={() => { setAbierto(f.id); setNombreAbierto(f.name); }}
                onExcluir={() => { void cambiarExclusion(f.id, !f.excluido); }}
              />
            ))}
          </div>

          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-sereno-dark/50 border-b border-gray-100 bg-sereno-blue/10">
                  <th className="py-2 px-3 font-medium">Cliente</th>
                  {/* Noches y «cliente desde» solo desde xl: son las dos
                      columnas menos consultadas y son las que hacían que la
                      tabla arrastrara de lado entre 1024 y 1280. */}
                  {ORDENES.filter((o) => o.valor !== 'name').map((o) => (
                    <th key={o.valor}
                      className={classNames('py-2 px-3 font-medium',
                        (o.valor === 'nights' || o.valor === 'firstCheckIn') && 'hidden xl:table-cell')}>
                      <button onClick={() => cambiarOrden(o.valor)}
                        className={classNames(
                          'inline-flex items-center gap-1 hover:text-sereno-dark',
                          orden === o.valor && 'text-sereno-dark font-semibold',
                        )}>
                        {o.etiqueta}
                        <ArrowUpDown size={11} className={orden === o.valor ? 'opacity-100' : 'opacity-30'} />
                      </button>
                    </th>
                  ))}
                  <th className="py-2 px-2 w-9" />
                  <th className="py-2 px-3 font-medium">Alojamientos</th>
                </tr>
              </thead>
              <tbody>
                {listado.data.items.map((f: HuespedFila) => (
                  <tr key={f.id}
                    onClick={() => { setAbierto(f.id); setNombreAbierto(f.name); }}
                    className="group border-b border-gray-50 last:border-0 hover:bg-sereno-blue/10 cursor-pointer align-top">
                    <td className="py-2 px-3">
                      <div className="font-medium flex items-center gap-1.5 flex-wrap">
                        {f.name || <span className="text-sereno-dark/30">Sin nombre</span>}
                        {f.isRepeat && <Etiqueta tone="good"><Repeat size={10} />repite</Etiqueta>}
                        {f.fichas > 1 && <Etiqueta><Layers size={10} />{f.fichas} fichas</Etiqueta>}
                        {f.revisar && <Etiqueta tone="warn"><AlertTriangle size={10} />revisar</Etiqueta>}
                        {f.llamadaNegativa && <Etiqueta tone="bad"><MessageSquare size={10} />queja</Etiqueta>}
                      </div>
                      <div className="text-xs text-sereno-dark/45 mt-0.5 truncate max-w-[200px] xl:max-w-[320px]"
                        title={[f.phone ? `+${f.phone}` : null, ...f.emails, f.country].filter(Boolean).join(' · ')}>
                        {[f.phone ? `+${f.phone}` : null, f.emails[0], f.country]
                          .filter(Boolean).join(' · ')}
                      </div>
                    </td>
                    <td className="py-2 px-3 tabular-nums whitespace-nowrap">{formatCurrency(f.revenue)}</td>
                    <td className="py-2 px-3 tabular-nums">
                      {f.stays}
                      {f.cancelled > 0 && <span className="text-xs text-sereno-dark/35"> (+{f.cancelled} canc.)</span>}
                    </td>
                    <td className="py-2 px-3 tabular-nums hidden xl:table-cell">{f.nights}</td>
                    <td className="py-2 px-3"><Estrellas nota={f.rating} /></td>
                    <td className="py-2 px-3 tabular-nums">
                      {f.llamadas || <span className="text-sereno-dark/25">—</span>}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap text-xs">
                      {f.lastCheckOut ? formatDate(f.lastCheckOut) : '—'}
                    </td>
                    <td className="py-2 px-3 whitespace-nowrap text-xs hidden xl:table-cell">
                      {f.firstCheckIn ? f.firstCheckIn.slice(0, 4) : '—'}
                    </td>
                    {/* Solo los dos primeros: hay un «huésped» que es el
                        propietario, con 111 estancias repartidas por decenas de
                        alojamientos, y volcar la lista entera le daba a su fila
                        el alto de media pantalla. El resto va en el title. */}
                    {/* Excluir desde la propia fila: la criba de cuentas de
                        prueba y propietarios se hace repasando la lista, no
                        abriendo ficha por ficha. Aparece al pasar por encima
                        para no ensuciar la tabla, y siempre si ya está
                        excluido, que es como se deshace. */}
                    <td className="py-2 px-2 w-9" onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        onClick={async () => { await cambiarExclusion(f.id, !f.excluido); }}
                        title={f.excluido
                          ? (f.motivo ? `Excluido: ${f.motivo} — pulsa para volver a incluir` : 'Volver a incluir en las métricas')
                          : 'Excluir de la vista y de las métricas'}
                        className={classNames(
                          'grid place-items-center h-7 w-7 rounded transition',
                          f.excluido
                            ? 'text-emerald-700 hover:bg-emerald-50'
                            : 'text-sereno-dark/30 opacity-0 group-hover:opacity-100 hover:text-amber-700 hover:bg-amber-50 focus:opacity-100',
                        )}
                      >
                        {f.excluido ? <Undo2 size={14} /> : <EyeOff size={14} />}
                      </button>
                    </td>
                    <td className="py-2 px-3 text-xs text-sereno-dark/55 max-w-[130px] xl:max-w-[220px]"
                      title={f.listings.join(', ')}>
                      {f.listings.length === 0 ? '—' : (
                        <>
                          {f.listings.slice(0, 2).join(', ')}
                          {f.listings.length > 2 && (
                            <span className="text-sereno-dark/35"> +{f.listings.length - 2} más</span>
                          )}
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100 text-xs">
            <span className="text-sereno-dark/45">
              Página {page} de {totalPaginas}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page <= 1}
                className="btn-ghost p-1 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <button onClick={() => setPage((p) => Math.min(totalPaginas, p + 1))} disabled={page >= totalPaginas}
                className="btn-ghost p-1 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          </div>
        </div>
      )}

      {abierto && (
        <Modal
          isOpen
          onClose={() => { setAbierto(null); setLlamada(null); setNombreAbierto(''); }}
          title={llamada || cargandoLlamada ? 'Llamada' : nombreAbierto || 'Huésped'}
          size={llamada || cargandoLlamada ? '2xl' : 'xl'}
        >
          {cargandoLlamada ? (
            <LoadingSpinner />
          ) : llamada ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setLlamada(null)}
                className="inline-flex items-center gap-1.5 text-xs text-sereno-dark/60 hover:text-sereno-dark"
              >
                <ArrowLeft size={14} />Volver a la ficha
              </button>
              <ContenidoLlamada
                detalle={llamada}
                onGuardar={async (nueva) => {
                  await api.marcarLlamada(llamada.id, nueva);
                  setLlamada({ ...llamada, guardada: nueva });
                }}
              />
            </div>
          ) : (
            <Ficha id={abierto} onAbrirLlamada={abrirLlamada} onExcluir={cambiarExclusion}
              onNombre={setNombreAbierto} />
          )}
        </Modal>
      )}
    </div>
  );
}
